import styled from "@emotion/styled";
import { Box } from "@otrium/core";
import { FloatingLabel } from "src/atoms/DropDown/Dropdown.styled";
import { scrollbar } from "src/scrollbar";

export { FloatingLabel };

export const SizeSelectorContainer = styled(Box)`
  position: relative;
  width: 100%;
`;

export const SelectedSize = styled.span<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme, $isOpen }) =>
    $isOpen ? theme.colors.tone.black : theme.colors.tone.anthracite};
  flex: 1;
  text-align: left;
`;

export const BrandSize = styled.span`
  &::after {
    content: "/";
    color: ${({ theme }) => theme.colors.tone.silver};
    margin: 0 6px;
  }
`;

export const SizeSelectorButton = styled.button<{
  isOpen?: boolean;
  hasError?: boolean;
  hasValue?: boolean;
}>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 48px;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.tone.white};
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.semantic.red : theme.colors.tone.silver};
  border-radius: ${({ isOpen }) => (isOpen ? `4px 4px 0 0` : `4px`)};
  cursor: pointer;
  transition: border-color 0.2s ease;
  position: relative;
  text-align: left;
  font-size: 16px;
  line-height: 24px;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    right: 15px;
    transform: translateY(-50%)
      ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0)")};
    display: inline-block;
    -webkit-mask-image: url(/static/images/chevron-down.svg);
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    -webkit-mask-size: contain;
    mask-image: url(/static/images/chevron-down.svg);
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    background-color: ${({ theme, isOpen }) =>
      isOpen ? theme.colors.tone.black : theme.colors.tone.anthracite};
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.tone.sand};
    cursor: default;
    color: ${({ theme }) => theme.colors.tone.anthraciteLight};
  }
`;

export const DropdownContainer = styled(Box)<{ isOpen: boolean }>`
  width: 100%;
  background: ${({ theme }) => theme.colors.tone.white};
  border: 1px solid ${({ theme }) => theme.colors.tone.silver};
  border-top: none;
  border-radius: 0 0 4px 4px;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
  max-height: ${({ isOpen }) => (isOpen ? "400px" : "0")};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  overflow: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  transition: max-height 0.3s ease, opacity 0.3s ease;
`;

export const HeaderRow = styled(Box)`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
`;

export const ColumnHeader = styled(Box)<{
  isClickable?: boolean;
  isOpen?: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  font-size: ${({ theme }) => theme.fontSizes.fontSize14};
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.tone.anthracite};
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};
  background: ${({ theme }) => theme.colors.tone.white};
  min-height: 48px;
  white-space: nowrap;

  &:last-of-type {
    border-left: ${({ theme, isOpen }) =>
      isOpen ? "none" : `1px solid ${theme.colors.tone.silver}`};
    border-bottom: ${({ theme, isOpen }) =>
      isOpen ? "none" : `1px solid ${theme.colors.tone.silver}`};
    border-radius: 0 0 0 8px;
    background: ${({ isOpen, theme }) =>
      isOpen ? theme.colors.tone.sand : theme.colors.tone.white};
    box-shadow: ${({ isOpen, theme }) =>
      isOpen ? `0 -4px 4px -4px ${theme.colors.tone.marble} inset` : "none"};
  }
`;

export const SchemaButton = styled.button<{ isOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.fontSize14};
  font-weight: ${({ isOpen }) => (isOpen ? 600 : 400)};
  color: ${({ theme }) => theme.colors.tone.black};
`;

export const SchemaChevron = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  -webkit-mask-image: url(/static/images/chevron-down.svg);
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-image: url(/static/images/chevron-down.svg);
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  background-color: ${({ theme }) => theme.colors.tone.black};
`;

export const CloseIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg g {
    stroke: ${({ theme }) => theme.colors.tone.black};
  }
`;

export const SizeListWrapper = styled.div`
  grid-column: 1 / -1;
  grid-row: 2;
  max-height: 220px;
  position: relative;
  display: grid;
  grid-template-columns: subgrid;
  align-self: start;
  z-index: 2;
  overflow-y: auto;
  overflow-x: hidden;
  ${scrollbar}
`;

export const SizeList = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
`;

export const BrandSizeColumnSpacer = styled.div<{ $isVisible?: boolean }>`
  grid-column: 1;
  grid-row: 2;
  pointer-events: none;
  display: ${({ $isVisible }) => ($isVisible ? "block" : "none")};
`;

export const SizeCell = styled.span<{
  $isSelected?: boolean;
  $isOutOfStock?: boolean;
}>`
  display: flex;
  align-items: center;
  min-height: 40px;
  padding-left: 16px;
  font-size: 16px;
  line-height: 24px;
  text-align: left;
  position: relative;
  color: ${({ theme, $isOutOfStock }) =>
    $isOutOfStock ? theme.colors.tone.silver : theme.colors.tone.black};
  text-decoration-line: ${({ $isOutOfStock }) =>
    $isOutOfStock ? "line-through" : "none"};

  &:last-of-type {
    color: ${({ theme, $isSelected, $isOutOfStock }) =>
      $isOutOfStock
        ? theme.colors.tone.silver
        : $isSelected
        ? theme.colors.tone.black
        : theme.colors.tone.anthracite};
  }
`;

export const StockIndicator = styled.span<{ $isSelected?: boolean }>`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: ${({ theme }) => theme.fontSizes.fontSize14};
  line-height: 20px;
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.tone.black : theme.colors.tone.anthraciteLight};
  white-space: nowrap;
`;

export const NotifyMeIndicator = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: ${({ theme }) => theme.fontSizes.fontSize14};
  font-weight: 600;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.tone.black};
  white-space: nowrap;
`;

export const SizeRow = styled.button<{
  isSelected?: boolean;
  isOutOfStock?: boolean;
}>`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  min-height: 40px;
  padding: 0;
  margin: 0;
  border: none;
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.tone.sand : "transparent"};
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    font-weight: ${({ isOutOfStock }) => (isOutOfStock ? 400 : 600)};
    background: ${({ theme }) => theme.colors.tone.sand};

    ${SizeCell} {
      color: ${({ theme, isOutOfStock }) =>
        isOutOfStock ? theme.colors.tone.silver : theme.colors.tone.black};
    }

    ${StockIndicator} {
      color: ${({ theme }) => theme.colors.tone.black};
    }

    ${NotifyMeIndicator} {
      text-decoration: underline;
    }
  }
`;

export const SizeSchemaOverlay = styled(Box)<{ isVisible: boolean }>`
  grid-column: 1 / -1;
  grid-row: 2;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
  z-index: 5;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};
  pointer-events: ${({ isVisible }) => (isVisible ? "auto" : "none")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

export const SizeSchemaDropdownWrapper = styled.div`
  grid-column: 2;
  grid-row: 2;
  position: relative;
  pointer-events: none;
  z-index: 11;
`;

export const SizeSchemaDropdown = styled(Box)<{ isOpen: boolean }>`
  max-height: 135px;
  padding: 8px;
  margin-bottom: 8px;
  background: ${({ theme }) => theme.colors.tone.white};
  border-top: none;
  border-bottom-left-radius: 10px;
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  flex-direction: column;
  overflow: hidden;
  pointer-events: auto;
`;

export const SizeSchemaMappingWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${scrollbar}
`;

export const SizeSchemaMapping = styled.button<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 2px 0 0 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  line-height: 24px;
  color: ${({ theme }) => theme.colors.tone.black};
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.tone.sand};
    font-weight: 600;
  }
`;

export const CheckIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  right: 4px;
  position: relative;
`;
